import React, { useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { brandProducts } from './brand-products';
import ProductPhoto from './ProductPhoto';
import { designFor } from './product-designs';
import './item-detail.css';

const narratives = {
  'scorpion-chest-tee': ['SCORPION / CHEST', 'サソリを胸元のアクセントに。小さなシンボルが印象を残す、デザインスタディ。'],
  'scorpion-back-tee': ['SCORPION / BACK', '背面にサソリの存在感を置く構成。振り返ったときのグラフィックを考えたデザイン案。'],
  'jellyfish-hem-tee': ['JELLYFISH / HEM', 'クラゲの輪郭と余白の関係。裾まわりのグラフィックを探るデザインスタディ。'],
  'snake-chest-tee': ['SNAKE / CHEST', 'ヘビの曲線を小さな印に。胸元の配置をテーマにしたグラフィック案。'],
  'snake-back-tee': ['SNAKE / BACK', '背面を舞台にヘビのラインを見せる。シルエットを活かすデザイン案。'],
  'puffer-mug': ['PUFFER / OBJECT', 'フグのまるい輪郭を日常の道具へ。マグカップを想定したビジュアルスタディ。'],
  'snake-tumbler': ['SNAKE / OBJECT', 'ヘビのグラフィックを立体の表面で考える。タンブラーを想定したデザイン案。']
};
const categoryFor = p => p.category === 'mug' ? 'home' : p.category === 'tumbler' ? 'drinkware' : 'apparel';
const labelFor = product => product.category === 'mug' ? 'HOME / MUG STUDY' : product.category === 'tumbler' ? 'DRINKWARE / TUMBLER STUDY' : 'APPAREL / T-SHIRT STUDY';

export default function ItemDetail() {
  const {slug} = useParams();
  const [query] = useSearchParams();
  const pageRef = useRef(null);
  const photoDialogRef = useRef(null);
  const index = brandProducts.findIndex(item => item.slug === slug);
  useEffect(() => {
    if (photoDialogRef.current?.open) photoDialogRef.current.close();
    if (pageRef.current) pageRef.current.scrollTop = 0;
    document.title = index < 0 ? 'ITEM NOT FOUND | PARALYZE AREA' : brandProducts[index].name + ' | PARALYZE AREA';
    return () => { document.title = 'PARALYZE AREA'; };
  }, [index, slug]);
  if (index === -1) return <main className="item-page item-missing"><Link to="/catalog">← アイテム一覧に戻る</Link><h1>ITEM NOT FOUND</h1><p>指定したデザインは見つかりません。</p></main>;
  const product = brandProducts[index];
  const [study, description] = narratives[slug];
  const category = query.get('category');
  const context = category === categoryFor(product) ? category : null;
  const back = '/catalog' + (context ? '?category=' + context : '') + '#' + slug;
  const detailUrl = item => '/items/' + item.slug + (context && context === categoryFor(item) ? '?category=' + context : '');
  const motif = slug.split('-')[0] + '-';
  const related = [...brandProducts.filter(p => p.slug !== slug && p.slug.startsWith(motif)), ...brandProducts.filter(p => p.slug !== slug && !p.slug.startsWith(motif))].slice(0,3);
  const filteredSequence = context ? brandProducts.filter(item => categoryFor(item) === context) : brandProducts;
  const sequence = filteredSequence.length > 1 ? filteredSequence : brandProducts;
  const position = sequence.findIndex(item => item.slug === slug);
  const previous = sequence[(position + sequence.length - 1) % sequence.length];
  const next = sequence[(position + 1) % sequence.length];
  return <main className="item-page" ref={pageRef}>
    <header className="item-nav">
      <Link className="item-wordmark" to="/"><img src="/brand/scorpion-symbol.svg" alt=""/>PARALYZE AREA</Link>
      <nav aria-label="アイテム詳細のナビゲーション"><Link to={back}>すべてのアイテム</Link><Link to={detailUrl(next)}>次を見る →</Link></nav>
    </header>
    <div className="item-breadcrumb"><Link to={back}>DROP 001 / ITEMS</Link><span aria-hidden="true">/</span><span>{String(index + 1).padStart(2,'0')} OF 07</span></div>
    <article className="item-layout">
      <div className="item-visual">
        <button type="button" className="item-visual-open" onClick={() => photoDialogRef.current?.showModal()} aria-label={product.name + ' の掲載イメージを拡大する'}>
          <ProductPhoto product={product} loading="eager"/><span className="item-photo-hint">拡大して見る ＋</span>
        </button>
        <span className="item-visual-index">{String(index + 1).padStart(2,'0')} / 07</span>
      </div>
      <div className="item-information">
        <p className="item-eyebrow">{labelFor(product)}</p>
        <h1>{product.name}</h1>
        <p className="item-study">{study}</p>
        <p className="item-description">{description}</p>
        <button type="button" className="item-zoom-action" onClick={() => photoDialogRef.current?.showModal()}>掲載イメージを拡大する ＋</button>
        <div className="item-status-block"><strong>AI MOCKUP · 販売準備中</strong><p>画像は暫定的なデザインイメージです。実商品・仕様・価格・在庫・発売日・購入先は未確定です。</p></div>
        <div className="item-actions"><Link className="item-primary-action" to={back}>← この商品の位置に戻る</Link><Link className="item-secondary-action" to={'/museum?exhibit=' + encodeURIComponent(product.slug)}>展示空間で見る ↗</Link></div>
        <p className="item-action-note">展示空間は鑑賞用です。購入・予約はできません。</p>
        <a href="https://www.tiktok.com/@paralyze_area" target="_blank" rel="noopener noreferrer" style={{display:'inline-flex',alignItems:'center',minHeight:44,fontWeight:800,fontSize:12,textDecoration:'underline'}}>公式SNSでブランドの活動を見る ↗</a>
      </div>
    </article>
    <dialog ref={photoDialogRef} className="item-image-dialog" aria-label={product.name + ' の掲載イメージ'}
      onClick={event => { if (event.target === photoDialogRef.current) photoDialogRef.current?.close(); }}>
      <button className="item-dialog-close" type="button" onClick={() => photoDialogRef.current?.close()} aria-label="拡大表示を閉じる">閉じる ×</button>
      <div className="item-dialog-photo"><ProductPhoto product={product} loading="eager"/></div>
      <p>{product.name} / AI MOCKUP · 販売準備中。実物写真ではありません。</p>
    </dialog>
    <nav aria-label="前後のデザインを見る" style={{display:'grid',gridTemplateColumns:'1fr 1fr',background:'#edc000',color:'#111'}}>
      <Link to={detailUrl(previous)} style={{padding:'24px 4vw',minHeight:95,borderTop:'1px solid #111',overflowWrap:'anywhere'}}><span style={{display:'block',fontSize:10,marginBottom:9}}>← PREVIOUS</span><strong>{previous.name}</strong></Link>
      <Link to={detailUrl(next)} style={{padding:'24px 4vw',minHeight:95,borderTop:'1px solid #111',borderLeft:'1px solid #111',textAlign:'right',overflowWrap:'anywhere'}}><span style={{display:'block',fontSize:10,marginBottom:9}}>NEXT →</span><strong>{next.name}</strong></Link>
    </nav>
    <section className="item-related" aria-labelledby="related-title">
      <div className="item-related-heading"><div><p>DROP 001 / KEEP EXPLORING</p><h2 id="related-title">OTHER STUDIES</h2></div><Link to="/catalog">7点すべてを見る ↗</Link></div>
      <div className="item-related-grid">{related.map(item => <Link className="item-related-card" to={detailUrl(item)} key={item.slug}>
        <div className="item-related-image"><ProductPhoto product={item}/></div><small className="item-related-type">{item.slug.startsWith(motif) ? '同じモチーフ' : '別のアイテム'}</small><span>{item.name}</span><span>詳細を見る ↗</span>
      </Link>)}</div>
    </section>
    <footer className="item-footer"><Link to="/">PARALYZE AREA</Link><span>AI MOCKUP / COMING SOON</span><a href="https://eeek-inc.com/" target="_blank" rel="noopener noreferrer">運営会社 ↗</a></footer>
  </main>;
}
